import { FocusEventHandler, forwardRef, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { media } from 'context';
import Animate from 'ds/Animate';
import IconButton from 'ds/IconButton';
import Layout from 'ds/Layout';
import {
  selectAIAssistantConversationRequestStateById,
  selectInputFocusOrdinal,
  selectNewConversationRequestState
} from 'store/AIAssistant/selectors';
import { useAppSelector } from 'store/hooks';

interface Props {
  handleSubmit: () => void;
  input: string;
  onChange: (value: string) => void;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  disableAnimation?: boolean;
  isExitAnimationActive?: boolean;
  autoFocus?: boolean;
}

interface RouteParams {
  id?: string;
}

const AIAssistantInput = forwardRef<HTMLInputElement, Props>(
  ({ onChange, input, handleSubmit, onFocus, onBlur, disableAnimation, isExitAnimationActive, autoFocus }, ref) => {
    const params = useParams<RouteParams>();
    const conversationId = params.id ? parseInt(params.id) : undefined;
    const requestState = useAppSelector(selectAIAssistantConversationRequestStateById(conversationId || -1));
    const newConversationRequestState = useAppSelector(selectNewConversationRequestState);
    const inputFocusOrdinal = useSelector(selectInputFocusOrdinal);
    const { isMobile } = useContext(media);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
      if (inputFocusOrdinal === 0) return;

      if (ref && 'current' in ref) {
        ref.current?.focus();
      }
    }, [inputFocusOrdinal]); // eslint-disable-line react-hooks/exhaustive-deps

    const Content = (
      <Layout align="center" borderRadius={1000} position="relative" width="100%" {...(isMobile ? { gap: 12 } : {})}>
        <input
          className="AIAssistant-threadInput"
          autoFocus={autoFocus || (conversationId !== undefined && !isMobile ? true : false)}
          value={input}
          onChange={e => onChange(e.target.value)}
          onKeyDown={({ key }) => {
            if (ref && 'current' in ref) {
              if (document.activeElement !== ref.current) {
                return;
              }
            }

            if (!input) return;

            if (key === 'Enter') {
              handleSubmit();
            }
          }}
          onFocus={e => {
            setIsFocused(true);
            onFocus?.(e);
          }}
          autoComplete="off"
          ref={ref}
          spellCheck={false}
          placeholder={conversationId !== undefined ? 'Send a message...' : 'Ask me about your office...'}
          onBlur={e => {
            setIsFocused(false);
            onBlur?.(e);
          }}
          style={{
            boxShadow: isFocused ? '2px 0 22px 0 rgba(34.1176, 28.6275, 42.7451, 0.1200)' : 'none',
            paddingRight: 48
          }}
        />
        <Layout
          width="100%"
          position="absolute"
          align="center"
          top={0}
          bottom={0}
          left={0}
          __style={{ pointerEvents: 'none' }}
        >
          <Layout position="absolute" right={12} top={0} bottom={0} align="center" __style={{ pointerEvents: 'auto' }}>
            <IconButton
              size="sm"
              type="primaryBlue"
              disabled={
                !input.trim() ||
                (conversationId !== undefined && requestState === 'in_progress') ||
                newConversationRequestState === 'in_progress'
              }
              name="upArrow"
              onClick={handleSubmit}
              stroke={2}
            />
          </Layout>
        </Layout>
      </Layout>
    );

    return (
      <Animate
        duration={isExitAnimationActive ? 400 : 300}
        animation={isExitAnimationActive ? 'fade-down' : 'fade-up'}
        disableAnimation={disableAnimation}
        delay={isExitAnimationActive ? 300 : 20}
      >
        {Content}
      </Animate>
    );
  }
);

export default AIAssistantInput;
